/**
 * Dropzone de capturas: click, drag & drop y paste.
 *
 * El drag & drop de ARCHIVOS es nuevo en el repo (sólo había DnD de elementos de UI con dnd-kit),
 * y tiene dos trampas que si no se resuelven se comen el formulario entero — ver los comentarios
 * del contador de profundidad y del guard a nivel documento.
 */
import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {ACCEPTED_TYPES, MAX_FILES, imageFilesFrom} from './feedbackFiles';

export const FeedbackAttachments = ({attachments, rejected, onAdd, onRemove}) => {
    const {t} = useTranslation();
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);
    // `dragleave` dispara al cruzar CADA hijo del dropzone, no sólo al salir. Sin contar la
    // profundidad, el highlight parpadea sin parar mientras el usuario mueve el mouse adentro.
    const dragDepthRef = useRef(0);

    /**
     * Si el usuario suelta el archivo 2px afuera de la zona, el navegador NAVEGA al archivo y se
     * lleva puesto el formulario entero — texto, audio y capturas. Es la pérdida de datos más
     * tonta y más frecuente de un dropzone.
     */
    useEffect(() => {
        const prevent = (event) => event.preventDefault();
        document.addEventListener('dragover', prevent);
        document.addEventListener('drop', prevent);
        return () => {
            document.removeEventListener('dragover', prevent);
            document.removeEventListener('drop', prevent);
        };
    }, []);

    const handleDrop = (event) => {
        event.preventDefault();
        dragDepthRef.current = 0;
        setDragActive(false);
        const files = imageFilesFrom(event.dataTransfer && event.dataTransfer.files);
        if (files.length) onAdd(files);
    };

    return (
        <div className="feedback-attachments">
            <div
                className={`feedback-dropzone ${dragActive ? '-over' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        if (fileInputRef.current) fileInputRef.current.click();
                    }
                }}
                onDragEnter={(event) => {
                    event.preventDefault();
                    dragDepthRef.current += 1;
                    setDragActive(true);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDragLeave={(event) => {
                    event.preventDefault();
                    dragDepthRef.current -= 1;
                    if (dragDepthRef.current <= 0) {
                        dragDepthRef.current = 0;
                        setDragActive(false);
                    }
                }}
                onDrop={handleDrop}
            >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <rect x="4" y="5" width="16" height="14" rx="2"/>
                    <path d="M4 15l4-4 3 3 4-5 5 6"/>
                    <circle cx="9" cy="9" r="1"/>
                </svg>
                <p>{dragActive ? t('feedback.attach.drop_here') : t('feedback.attach.hint')}</p>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES}
                    multiple
                    hidden
                    onChange={(event) => {
                        const files = imageFilesFrom(event.target.files);
                        if (files.length) onAdd(files);
                        // Reset: sin esto, elegir el MISMO archivo dos veces seguidas no dispara
                        // `change` la segunda vez.
                        event.target.value = '';
                    }}
                />
            </div>

            {attachments.length > 0 && (
                <ul className="feedback-thumbs">
                    {attachments.map((attachment) => (
                        // `key` = id generado, JAMÁS el índice: quitar por índice re-mapea la lista
                        // y React reusa el DOM equivocado (el thumbnail que ves deja de ser el
                        // archivo que se manda).
                        <li key={attachment.id} className="feedback-thumb">
                            <img src={attachment.previewUrl} alt={attachment.name}/>
                            <button
                                type="button"
                                onClick={() => onRemove(attachment.id)}
                                aria-label={t('feedback.attach.remove', {name: attachment.name})}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <span className="feedback-visually-hidden" aria-live="polite">
                {attachments.length > 0 && t('feedback.attach.count', {count: attachments.length})}
            </span>

            {/* El rechazo NUNCA es silencioso: si arrastró 8 con tope de 5, decimos cuáles no
                entraron y por qué. Si no, el usuario cree que mandó algo que no mandó. */}
            {rejected.length > 0 && (
                <ul className="feedback-rejected" role="alert">
                    {Object.entries(groupByReason(rejected)).map(([reasonKey, names]) => (
                        <li key={reasonKey}>
                            {t(reasonKey, {names: names.join(', '), max: MAX_FILES,
                                           interpolation: {escapeValue: false}})}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

function groupByReason(rejected) {
    return rejected.reduce((acc, item) => {
        acc[item.reasonKey] = acc[item.reasonKey] || [];
        acc[item.reasonKey].push(item.name);
        return acc;
    }, {});
}

export default FeedbackAttachments;
